import {
  JobAvailabilityType,
  SavedSearch,
  SitterSearchRecurring,
} from 'us-web-services';

import { DAYS_OF_WEEK, TIMES_OF_DAY } from '../pages/Sitters/constants';
import { LabeledId } from '../models/FamilyRegistrationStore/FamilyRegistrationStore';

export interface NuxFilterSource {
  availabilityType?: string;
  startString?: string;
  endString?: string;
  startTime?: string;
  endTime?: string;
  recurring?: { days?: Array<{ weekday?: { code?: string } }> };
  languages: LabeledId[];
  teachingSkills: LabeledId[];
  specialSituations: LabeledId[];
  specialServices: LabeledId[];
  specialExperiences: LabeledId[];
}

const MORNING_END_HOUR = 12;
const AFTERNOON_END_HOUR = 17;
const ALL_TIME_OF_DAY_IDS = [TIMES_OF_DAY.MORNING.id, TIMES_OF_DAY.AFTERNOON.id, TIMES_OF_DAY.NIGHT.id];

const parseHour = (time?: string): number | undefined => {
  if (!time) {
    return undefined;
  }

  const [hourPart, minutePart] = time.split(':');
  const hour = parseInt(hourPart, 10);

  if (Number.isNaN(hour)) {
    return undefined;
  }

  const minute = parseInt(minutePart ?? '0', 10);

  return hour + (Number.isNaN(minute) ? 0 : minute) / 60;
};

export const timeOfDayIdsFromTimeRange = (startTime?: string, endTime?: string): number[] => {
  const start = parseHour(startTime);
  const end = parseHour(endTime);

  if (start === undefined && end === undefined) {
    return [];
  }

  const rangeStart = start ?? 0;
  const rangeEnd = end ?? 24;
  const ids: number[] = [];

  if (rangeStart < MORNING_END_HOUR) {
    ids.push(TIMES_OF_DAY.MORNING.id);
  }
  if (rangeStart < AFTERNOON_END_HOUR && rangeEnd > MORNING_END_HOUR) {
    ids.push(TIMES_OF_DAY.AFTERNOON.id);
  }
  if (rangeEnd > AFTERNOON_END_HOUR) {
    ids.push(TIMES_OF_DAY.NIGHT.id);
  }

  return ids;
};

const recurringTimeOfDayIds = (familyRegistration: NuxFilterSource): number[] => {
  const ids = timeOfDayIdsFromTimeRange(familyRegistration.startTime, familyRegistration.endTime);

  return ids.length ? ids : ALL_TIME_OF_DAY_IDS;
};

const DATE_TIME_WITH_SECONDS = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
const DATE_TIME_WITHOUT_SECONDS = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/;

const ensureSeconds = (value?: string): string | undefined => {
  if (!value) {
    return undefined;
  }
  if (DATE_TIME_WITH_SECONDS.test(value)) {
    return value;
  }
  if (DATE_TIME_WITHOUT_SECONDS.test(value)) {
    return `${value}:00`;
  }

  return undefined;
};

const endOfDayFrom = (startDateTime: string): string => `${startDateTime.slice(0, 10)} 23:59:59`;

const withId = (items: LabeledId[]): LabeledId[] => items.filter(item => item.id !== undefined);

const joinIds = (items: LabeledId[]): string =>
  items
    .map(item => item.id)
    .filter(id => id !== undefined)
    .join(',');

export const buildNuxFilterSearchParams = (familyRegistration: NuxFilterSource): Record<string, string> => {
  const params: Record<string, string> = {};

  if (familyRegistration.availabilityType === JobAvailabilityType.occasional) {
    const start = ensureSeconds(familyRegistration.startString);
    const end = ensureSeconds(familyRegistration.endString) ?? (start ? endOfDayFrom(start) : undefined);

    if (start) {
      params.start = start;
    }
    if (end) {
      params.end = end;
    }
  } else if (familyRegistration.recurring?.days?.length) {
    const value = recurringTimeOfDayIds(familyRegistration).join(',');

    familyRegistration.recurring.days.forEach(day => {
      const match = DAYS_OF_WEEK.find(d => d.code === day.weekday?.code);

      if (match) {
        params[match.filterKey] = value;
      }
    });
  }

  const languageIds = joinIds(familyRegistration.languages);
  if (languageIds) {
    params.languages = languageIds;
  }

  const teachingSkillIds = joinIds(familyRegistration.teachingSkills);
  if (teachingSkillIds) {
    params.teachingSkillIds = teachingSkillIds;
  }

  const specialSituationIds = joinIds(familyRegistration.specialSituations);
  if (specialSituationIds) {
    params.specialSituationIds = specialSituationIds;
  }

  const specialServiceIds = joinIds(familyRegistration.specialServices);
  if (specialServiceIds) {
    params.specialServiceIds = specialServiceIds;
  }

  // `certificationIds` carries us_special_experience ids — a platform-wide misnomer, not a bug.
  const specialExperienceIds = joinIds(familyRegistration.specialExperiences);
  if (specialExperienceIds) {
    params.certificationIds = specialExperienceIds;
  }

  return params;
};

export const buildNuxSavedSearchRecurring = (familyRegistration: NuxFilterSource): SitterSearchRecurring[] => {
  const days = familyRegistration.recurring?.days;

  if (!days?.length) {
    return [];
  }

  const timeOfDayIds = recurringTimeOfDayIds(familyRegistration);

  return days
    .map(day => {
      const match = DAYS_OF_WEEK.find(d => d.code === day.weekday?.code);

      if (!match) {
        return null;
      }

      return {
        dayOfWeek: { id: match.id, code: match.code, label: match.name },
        timeOfDayIds,
      };
    })
    .filter(Boolean) as SitterSearchRecurring[];
};

export const buildNuxSavedSearchFilters = (familyRegistration: NuxFilterSource): Partial<SavedSearch> => {
  const filters: Partial<SavedSearch> = {};

  const teachingSkill = withId(familyRegistration.teachingSkills)
    .map(item => ({ teachingSkillId: item.id, label: item.label }));
  if (teachingSkill.length) {
    filters.teachingSkill = teachingSkill;
  }

  const specialSituation = withId(familyRegistration.specialSituations)
    .map(item => ({ specialSituationId: item.id, label: item.label }));
  if (specialSituation.length) {
    filters.specialSituation = specialSituation;
  }

  const specialService = withId(familyRegistration.specialServices)
    .map(item => ({ specialServiceId: item.id, label: item.label }));
  if (specialService.length) {
    filters.specialService = specialService;
  }

  const specialExperience = withId(familyRegistration.specialExperiences)
    .map(item => ({ specialExperienceId: item.id, label: item.label }));
  if (specialExperience.length) {
    filters.specialExperience = specialExperience;
  }

  const languages = withId(familyRegistration.languages)
    .map(item => ({ languageId: item.id, label: item.label }));
  if (languages.length) {
    filters.languages = languages;
  }

  const recurring = buildNuxSavedSearchRecurring(familyRegistration);
  if (recurring.length) {
    filters.recurring = recurring;
  }

  return filters;
};
